/**
 * N3 · Competência 5 (Probabilidade) · task-model H1.30.K01
 *
 *   "Identificar, no espaço amostral do lançamento de dois dados, os resultados que
 *    formam um evento."
 *
 * A tabela 6 × 6 dos resultados de dois dados — um azul, um vermelho —, e o aluno clica
 * em TODAS as casas do evento pedido. É o passo que decide a probabilidade: contar os
 * casos favoráveis. E é nele que moram os erros clássicos, todos com nome:
 *  - soma e diferença: marcar uma casa só por par de números, como se (2, 5) e (5, 2)
 *    fossem o mesmo resultado;
 *  - "maior que" e "menor que": incluir a fronteira;
 *  - produto par: exigir os dois pares, ou esquecer que par × par é par;
 *  - "pelo menos um": ler como "exatamente um", ou olhar para um dado só.
 *
 * As casas não trazem número dentro: a coluna diz o dado azul, a linha, o vermelho. Com
 * a soma escrita em cada casa, o item viraria procurar números iguais.
 */

import { gcd } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  DADOS_EXCLUIU_DUPLO,
  DADOS_FRONTEIRA,
  DADOS_ORDEM,
  DADOS_PRODUTO_AMBOS_PARES,
  DADOS_PRODUTO_UM_PAR,
  DADOS_UM_DADO_SO,
} from '../../schema/errors.ts';
import type { ItemTemplate, Regiao } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

type Evento = 'soma' | 'somaMaior' | 'somaMenor' | 'diferenca' | 'produtoPar' | 'peloMenosUm';

export interface Params {
  readonly evento: Evento;
  readonly k: number;
}

const EVENTOS: readonly Evento[] = [
  'soma',
  'somaMaior',
  'somaMenor',
  'diferenca',
  'produtoPar',
  'peloMenosUm',
];

const VALORES: Readonly<Record<Evento, readonly number[]>> = {
  soma: [5, 6, 7, 8, 9],
  somaMaior: [7, 8, 9],
  somaMenor: [5, 6],
  diferenca: [1, 2, 3],
  produtoPar: [0],
  peloMenosUm: [1, 2, 3, 4, 5, 6],
};

function drawParams(rng: Rng): Params {
  const evento = rng.pick(EVENTOS);
  return { evento, k: rng.pick(VALORES[evento]) };
}

/** Um resultado: [dado azul, dado vermelho]. */
type Par = readonly [number, number];

const PARES: readonly Par[] = [1, 2, 3, 4, 5, 6].flatMap((v) =>
  [1, 2, 3, 4, 5, 6].map((a) => [a, v] as Par),
);
const idDe = ([a, v]: Par): string => `${a}-${v}`;
const parTxt = ([a, v]: Par): string => `(${a}, ${v})`;

function pertence(p: Params, [a, v]: Par): boolean {
  switch (p.evento) {
    case 'soma':
      return a + v === p.k;
    case 'somaMaior':
      return a + v > p.k;
    case 'somaMenor':
      return a + v < p.k;
    case 'diferenca':
      return Math.abs(a - v) === p.k;
    case 'produtoPar':
      return (a * v) % 2 === 0;
    case 'peloMenosUm':
      return a === p.k || v === p.k;
  }
}

const PEDIDO: Readonly<Record<Evento, (k: number) => string>> = {
  soma: (k) => `a soma dos dois números é ${k}`,
  somaMaior: (k) => `a soma dos dois números é maior que ${k}`,
  somaMenor: (k) => `a soma dos dois números é menor que ${k}`,
  diferenca: (k) => `a diferença entre o maior e o menor número é ${k}`,
  produtoPar: () => 'o produto dos dois números é par',
  peloMenosUm: (k) => `pelo menos um dos dados mostra o número ${k}`,
};

/** A segunda dica, própria de cada evento: um jeito de percorrer a tabela. */
const DICA_EVENTO: Readonly<Record<Evento, (k: number) => string>> = {
  soma: (k) =>
    `Percorra uma coluna de cada vez: com o dado azul mostrando 1, 2, 3…, que número do vermelho completa a soma ${k}?`,
  somaMaior: (k) =>
    `Localize primeiro as casas com soma exatamente ${k}: elas ficam de fora. As de soma maior ficam todas do mesmo lado delas.`,
  somaMenor: (k) =>
    `Localize primeiro as casas com soma exatamente ${k}: elas ficam de fora. As de soma menor ficam todas do mesmo lado delas.`,
  diferenca: () =>
    'Nas casas de uma mesma diagonal da tabela, a diferença entre os dois números é sempre a mesma.',
  produtoPar: () =>
    'Um produto só é ímpar quando os dois números são ímpares. Quais casas são essas? As outras têm produto par.',
  peloMenosUm: (k) =>
    `Pense em cada dado separadamente: em que casas o azul mostra ${k}? E em quais o vermelho mostra ${k}?`,
};

interface ErroTipico {
  readonly conjunto: readonly string[];
  readonly error: ErrorCode;
  readonly explanation: RichText;
}

function errosDe(p: Params, chave: readonly Par[]): ErroTipico[] {
  const ids = (xs: readonly Par[]): string[] => xs.map(idDe);
  const casas = (n: number): string => `são ${chave.length} casas, e não ${n}`;
  switch (p.evento) {
    case 'soma':
    case 'diferenca': {
      const abaixo = chave.filter(([a, v]) => a <= v);
      const acima = chave.filter(([a, v]) => a >= v);
      const ex = chave.find(([a, v]) => a < v) as Par;
      const explanation = [
        txt(
          `Você marcou uma casa só para cada par de números. Mas ${parTxt(ex)} e ${parTxt([ex[1], ex[0]])} são resultados ` +
            `diferentes: no primeiro, o dado azul mostra ${ex[0]} e o vermelho, ${ex[1]}; no segundo, é o contrário. ` +
            `Por isso, ${casas(abaixo.length)}.`,
        ),
      ];
      return [
        { conjunto: ids(abaixo), error: DADOS_ORDEM, explanation },
        { conjunto: ids(acima), error: DADOS_ORDEM, explanation },
      ];
    }
    case 'somaMaior':
    case 'somaMenor': {
      const maior = p.evento === 'somaMaior';
      const comIgual = PARES.filter(([a, v]) => (maior ? a + v >= p.k : a + v <= p.k));
      const ex = PARES.find(([a, v]) => a + v === p.k && a < v) as Par;
      return [
        {
          conjunto: ids(comIgual),
          error: DADOS_FRONTEIRA,
          explanation: [
            txt(
              `Você marcou também as casas em que a soma é exatamente ${p.k}, como ${parTxt(ex)}. ` +
                `"${maior ? 'Maior' : 'Menor'} que ${p.k}" deixa o próprio ${p.k} de fora: ${casas(comIgual.length)}.`,
            ),
          ],
        },
      ];
    }
    case 'produtoPar': {
      const ambos = PARES.filter(([a, v]) => a % 2 === 0 && v % 2 === 0);
      const um = PARES.filter(([a, v]) => (a % 2 === 0) !== (v % 2 === 0));
      return [
        {
          conjunto: ids(ambos),
          error: DADOS_PRODUTO_AMBOS_PARES,
          explanation: [
            txt(
              'Você marcou só as casas com os dois números pares. Mas basta um deles ser par: em (3, 4), o produto é ' +
                `3 × 4 = 12, que é par. Por isso, ${casas(ambos.length)}.`,
            ),
          ],
        },
        {
          conjunto: ids(um),
          error: DADOS_PRODUTO_UM_PAR,
          explanation: [
            txt(
              'Você deixou de fora as casas com os dois números pares, como (2, 4): o produto, 2 × 4 = 8, também é par. ' +
                `Por isso, ${casas(um.length)}.`,
            ),
          ],
        },
      ];
    }
    case 'peloMenosUm': {
      const n = p.k;
      const exatamente = PARES.filter(([a, v]) => (a === n) !== (v === n));
      const umDado = (qual: 'azul' | 'vermelho'): ErroTipico => ({
        conjunto: ids(PARES.filter(([a, v]) => (qual === 'azul' ? a : v) === n)),
        error: DADOS_UM_DADO_SO,
        explanation: [
          txt(
            `Você marcou só as casas em que o dado ${qual} mostra ${n}. Quando é o ${qual === 'azul' ? 'vermelho' : 'azul'} ` +
              `que mostra ${n}, também há pelo menos um ${n}: ${casas(6)}.`,
          ),
        ],
      });
      return [
        {
          conjunto: ids(exatamente),
          error: DADOS_EXCLUIU_DUPLO,
          explanation: [
            txt(
              `Você deixou de fora a casa (${n}, ${n}), em que os dois dados mostram ${n}. Nela também há pelo menos ` +
                `um ${n}: ${casas(exatamente.length)}.`,
            ),
          ],
        },
        umDado('azul'),
        umDado('vermelho'),
      ];
    }
  }
}

/** Lado da casa, em unidades do desenho. */
const C = 10;

function tabuleiro(): { backdrop: Shape[]; regioes: Regiao[] } {
  const backdrop: Shape[] = [];
  const regioes: Regiao[] = [];
  for (const [a, v] of PARES) {
    const x = (a - 1) * C;
    const y = (6 - v) * C;
    backdrop.push({ t: 'rect', at: { x, y, w: C, h: C }, role: 'auxiliary', fill: 'surface' });
    regioes.push({
      id: idDe([a, v]),
      rotulo: parTxt([a, v]),
      nome: `Dado azul ${a}, dado vermelho ${v}`,
      forma: [
        { x, y },
        { x: x + C, y },
        { x: x + C, y: y + C },
        { x, y: y + C },
      ],
    });
  }
  backdrop.push({ t: 'rect', at: { x: 0, y: 0, w: 6 * C, h: 6 * C }, role: 'given', fill: 'none' });
  for (let i = 1; i <= 6; i++) {
    backdrop.push({
      t: 'label',
      at: { x: (i - 0.5) * C, y: 6 * C },
      text: [strong(String(i))],
      anchor: 'n',
    });
    backdrop.push({
      t: 'label',
      at: { x: 0, y: (6.5 - i) * C },
      text: [strong(String(i))],
      anchor: 'w',
    });
  }
  backdrop.push({
    t: 'label',
    at: { x: 3 * C, y: 6 * C + 5.5 },
    text: [txt('Dado azul')],
    anchor: 'n',
    emphasis: 'axis',
  });
  backdrop.push({
    t: 'label',
    at: { x: -7.5, y: 3 * C },
    text: [txt('Dado vermelho')],
    anchor: 'c',
    rotate: 90,
    emphasis: 'axis',
  });
  return { backdrop, regioes };
}

function fracao(n: number, d: number): string {
  const g = gcd(n, d);
  return g > 1 ? `${n}/${d} = ${n / g}/${d / g}` : `${n}/${d}`;
}

export const dadosEvento: ItemTemplate<Params, null> = {
  id: templateId('h1-30-K01-dados-evento'),
  version: 1,
  taskModel: taskModelCode('H1.30.K01'),
  skills: [skillCode('H1.30'), skillCode('H1.28')],
  axis: 'II',
  level: 'N3',
  title: 'Eventos no lançamento de dois dados',
  responseModel: '2PL',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.evento === 'produtoPar' || p.evento === 'peloMenosUm') fontes.push('steps.3plus');
    return fontes;
  },
  build(p) {
    const chave = PARES.filter((x) => pertence(p, x));
    const { backdrop, regioes } = tabuleiro();
    const pedido = PEDIDO[p.evento](p.k);
    return {
      stem: [
        txt(
          'Dois dados comuns, um azul e um vermelho, são lançados ao mesmo tempo. Cada casa da tabela abaixo é um dos 36 ' +
            'resultados possíveis: a coluna diz o número do dado azul, e a linha, o do vermelho.',
        ),
      ],
      comando: [txt(`Clique em todas as casas dos resultados em que ${pedido}.`)],
      stimuli: [],
      interaction: {
        kind: 'region-select',
        descricao:
          'Tabela de 6 por 6 casas. As colunas são numeradas de 1 a 6, da esquerda para a direita, e correspondem ao dado ' +
          'azul; as linhas, de 1 a 6, de cima para baixo, correspondem ao dado vermelho.',
        backdrop,
        viewBox: { x: -11, y: -2, w: 6 * C + 14, h: 6 * C + 13 },
        regioes,
        oQue: 'casa',
        chave: chave.map(idDe),
        erros: errosDe(p, chave),
      },
      dicas: [
        [
          txt(
            'Cada casa é um resultado: a coluna é o número do dado azul, a linha é o do vermelho. (2, 5) e (5, 2) são casas diferentes.',
          ),
        ],
        [txt(DICA_EVENTO[p.evento](p.k))],
      ],
      rationale: [
        strong('Resolução. '),
        txt(`Os resultados em que ${pedido} ocupam ${chave.length} casas`),
        txt(chave.length <= 12 ? `: ${chave.map(parTxt).join(', ')}.` : '.'),
        br,
        txt(
          'Aqui, (a, v) quer dizer dado azul a e dado vermelho v: (2, 5) e (5, 2) são casas diferentes.',
        ),
        br,
        txt(
          `Como os 36 resultados são igualmente prováveis, a probabilidade desse evento é ${fracao(chave.length, 36)}.`,
        ),
      ],
      declared:
        p.evento === 'produtoPar'
          ? 'dificil'
          : p.evento === 'somaMaior' || p.evento === 'somaMenor'
            ? 'facil'
            : 'media',
    };
  },
};
